import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../lib/AuthContext';
import orangeLogo from '../assets/orangelog.png';

const T = {
  accent:  '#dd3333',
  navy:    '#13273F',
  border:  '#e8e8e8',
  white:   '#fff',
  meta:    '#777',
  lightBg: '#f4f4f4',
};

const inputSty = (err) => ({
  width: '100%', boxSizing: 'border-box',
  padding: '0.6rem 0.8rem',
  border: `1px solid ${err ? T.accent : '#ccc'}`,
  borderRadius: '5px', fontSize: '0.92rem',
  fontFamily: 'Arial, sans-serif', outline: 'none',
  background: '#fff',
});

const OBJECTIVES = [
  'Brand Awareness',
  'Drive Website Traffic',
  'Promote an Event',
  'Generate Leads / Inquiries',
  'Increase Sales / Conversions',
  'Community Engagement',
  'Recruit Employees',
  'Other',
];

const AUDIENCE_OPTIONS = [
  'Hyper-Local (single city or neighborhood)',
  'Regional (metro area / county)',
  'State-wide',
  'Multi-State',
  'National',
];

const BUDGET_OPTIONS = [
  'Under $500 / month',
  '$500 – $1,000 / month',
  '$1,000 – $2,500 / month',
  '$2,500 – $5,000 / month',
  '$5,000+ / month',
  'Not sure yet — looking for options',
];

const DURATION_OPTIONS = [
  '1 month (trial)',
  '3 months',
  '6 months',
  '12 months',
  'Ongoing / flexible',
];

const FORMAT_OPTIONS = [
  'Display / Banner Ad',
  'Sponsored Article (Native Content)',
  'Newsletter Sponsorship',
  'Homepage Featured Placement',
  'Genre / Category Page Sponsorship',
  'Not sure — please recommend',
];

const CATEGORY_OPTIONS = [
  'Local News',
  'State News',
  'National News',
  'Sports',
  'Technology',
  'All Categories',
];

const HEAR_OPTIONS = [
  'Search engine',
  'Social media',
  'Referral from another advertiser',
  'Referral from a reader',
  'PubSilo outreach',
  'News article or press',
  'Other',
];

function FieldGroup({ label, required, children }) {
  return (
    <div style={{ marginBottom: '1rem' }}>
      <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '700', color: '#333', fontFamily: 'Arial, sans-serif', marginBottom: '0.3rem' }}>
        {label}{required && <span style={{ color: T.accent, marginLeft: '2px' }}>*</span>}
      </label>
      {children}
    </div>
  );
}

function CheckGroup({ options, values, onChange }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 24px', marginTop: '6px' }}>
      {options.map(opt => (
        <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '0.88rem', color: '#333', cursor: 'pointer', fontFamily: 'Arial, sans-serif', userSelect: 'none' }}>
          <input
            type="checkbox"
            checked={values.includes(opt)}
            onChange={() => onChange(opt)}
            style={{ width: '15px', height: '15px', accentColor: T.accent, cursor: 'pointer', flexShrink: 0 }}
          />
          {opt}
        </label>
      ))}
    </div>
  );
}

function SectionHead({ label, color }) {
  const c = color || T.accent;
  return (
    <div style={{ fontSize: '11px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.09em', color: c, borderBottom: `2px solid ${c}`, paddingBottom: '6px', marginBottom: '1rem' }}>
      {label}
    </div>
  );
}

function AdvertisePage() {
  const navigate  = useNavigate();
  const { accessToken } = useAuth();
  const userType  = localStorage.getItem('userType');
  const username  = localStorage.getItem('username');

  const [form, setForm] = useState({
    contactName:  '',
    businessName: '',
    contactEmail: '',
    phone:        '',
    website:      '',
    objective:    '',
    audiences:    [],
    budget:       '',
    startDate:    '',
    duration:     '',
    formats:      [],
    categories:   [],
    description:  '',
    notes:        '',
    hearAbout:    '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done,    setDone]    = useState(false);
  const [serverError, setServerError] = useState('');

  const set = field => e => {
    setForm(f => ({ ...f, [field]: e.target.value }));
    setErrors(er => ({ ...er, [field]: '' }));
  };

  const toggle = field => value => {
    setForm(f => ({
      ...f,
      [field]: f[field].includes(value)
        ? f[field].filter(v => v !== value)
        : [...f[field], value],
    }));
  };

  const validate = () => {
    const e = {};
    if (!form.contactName.trim())  e.contactName  = 'Required';
    if (!form.businessName.trim()) e.businessName = 'Required';
    if (!form.contactEmail.trim()) e.contactEmail = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contactEmail)) e.contactEmail = 'Enter a valid email';
    if (!form.objective)           e.objective    = 'Please select an objective';
    if (form.audiences.length === 0) e.audiences  = 'Select at least one audience';
    if (!form.budget)              e.budget       = 'Please select a budget range';
    return e;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoading(true); setServerError('');
    try {
      await axios.post('/api/contact/advertiser', form);
      setDone(true);
    } catch (err) {
      setServerError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const siloHref = (() => {
    if (!accessToken) return null;
    if (userType === 'company') return `/adminHUD/${encodeURIComponent(localStorage.getItem('companyName') || '')}`;
    if (userType === 'user')   return `/${username}`;
    return null;
  })();

  return (
    <div style={{ minHeight: '100vh', background: T.lightBg, fontFamily: 'Arial, sans-serif' }}>

      {/* ── Navbar ── */}
      <nav style={{ background: '#111111', display: 'grid', gridTemplateColumns: '220px 1fr 300px', gridTemplateRows: '110px 36px', position: 'sticky', top: 0, zIndex: 1000, boxShadow: '0 2px 12px rgba(0,0,0,0.6)' }}>
        <div style={{ gridColumn: 1, gridRow: '1 / 3', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F41238', cursor: 'pointer' }} onClick={() => navigate('/')}>
          <img src={orangeLogo} alt="PubSilo" style={{ height: '90%', maxWidth: '210px', objectFit: 'contain' }} />
        </div>
        <div style={{ gridColumn: 2, gridRow: 1, background: T.navy, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
          <div style={{ color: '#aaa', fontSize: '12px', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Reach Your Audience</div>
          <div style={{ color: T.white, fontSize: '28px', fontWeight: '300', letterSpacing: '0.06em', lineHeight: 1 }}>Advertise on PubSilo</div>
        </div>
        <div style={{ gridColumn: 3, gridRow: '1 / 3', background: T.navy, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
          {accessToken && siloHref ? (
            <Link to={siloHref} style={{ background: T.accent, color: T.white, textDecoration: 'none', fontSize: '13px', fontWeight: '700', padding: '9px 18px', borderRadius: '5px', whiteSpace: 'nowrap' }}>
              {userType === 'company' ? 'Admin Page' : 'My Silo'}
            </Link>
          ) : (
            <>
              <Link to="/login" style={{ color: '#ccc', textDecoration: 'none', fontSize: '13px', fontWeight: '700', padding: '8px 15px', border: '1px solid #3a3a3a', borderRadius: '5px' }}>Login</Link>
              <Link to="/signup/reader" style={{ background: T.accent, color: T.white, textDecoration: 'none', fontSize: '13px', fontWeight: '700', padding: '9px 18px', borderRadius: '5px', whiteSpace: 'nowrap' }}>Sign Up</Link>
            </>
          )}
        </div>
        <div style={{ gridColumn: 2, gridRow: 2, background: '#0d1f33' }} />
      </nav>

      {/* ── Body ── */}
      <div style={{ maxWidth: '720px', margin: '3rem auto', padding: '0 1.5rem 5rem' }}>

        {done ? (
          <div style={{ background: T.white, borderRadius: '10px', padding: '3rem', textAlign: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>✅</div>
            <h2 style={{ fontSize: '1.4rem', fontWeight: '800', color: '#111', marginBottom: '0.75rem' }}>Inquiry Received!</h2>
            <p style={{ color: T.meta, fontSize: '0.95rem', lineHeight: '1.6', marginBottom: '1.5rem' }}>
              Thanks for your interest in advertising on PubSilo. Our team will review your campaign details and reach out to <strong>{form.contactEmail}</strong> within 1–2 business days.
            </p>
            <button onClick={() => navigate('/')} style={{ background: T.accent, color: T.white, border: 'none', borderRadius: '6px', padding: '0.75rem 2rem', fontWeight: '700', fontSize: '0.95rem', cursor: 'pointer' }}>
              Back to PubSilo
            </button>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: '2rem' }}>
              <h1 style={{ fontSize: '1.6rem', fontWeight: '900', color: '#111', marginBottom: '0.5rem' }}>Advertising Inquiry</h1>
              <p style={{ color: T.meta, fontSize: '0.92rem', lineHeight: '1.6' }}>
                Tell us about your campaign and we'll put together options that fit your audience, goals, and budget. Fields marked <span style={{ color: T.accent }}>*</span> are required.
              </p>
            </div>

            <form onSubmit={handleSubmit} noValidate>

              {/* ── Contact Information ── */}
              <div style={{ background: T.white, borderRadius: '10px', padding: '1.5rem', boxShadow: '0 1px 6px rgba(0,0,0,0.06)', marginBottom: '1.25rem' }}>
                <SectionHead label="Contact Information" color={T.accent} />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1rem' }}>
                  <FieldGroup label="Contact Name" required>
                    <input value={form.contactName} onChange={set('contactName')} style={inputSty(errors.contactName)} placeholder="Jane Smith" />
                    {errors.contactName && <div style={{ fontSize: '0.77rem', color: T.accent, marginTop: '3px' }}>{errors.contactName}</div>}
                  </FieldGroup>
                  <FieldGroup label="Business / Organization Name" required>
                    <input value={form.businessName} onChange={set('businessName')} style={inputSty(errors.businessName)} placeholder="Acme Corporation" />
                    {errors.businessName && <div style={{ fontSize: '0.77rem', color: T.accent, marginTop: '3px' }}>{errors.businessName}</div>}
                  </FieldGroup>
                  <FieldGroup label="Contact Email" required>
                    <input type="email" value={form.contactEmail} onChange={set('contactEmail')} style={inputSty(errors.contactEmail)} placeholder="jane@yourbusiness.com" />
                    {errors.contactEmail && <div style={{ fontSize: '0.77rem', color: T.accent, marginTop: '3px' }}>{errors.contactEmail}</div>}
                  </FieldGroup>
                  <FieldGroup label="Phone Number">
                    <input value={form.phone} onChange={set('phone')} style={inputSty()} placeholder="(555) 000-0000" />
                  </FieldGroup>
                </div>
                <FieldGroup label="Business Website">
                  <input value={form.website} onChange={set('website')} style={inputSty()} placeholder="https://yourbusiness.com" />
                </FieldGroup>
              </div>

              {/* ── Campaign Goals ── */}
              <div style={{ background: T.white, borderRadius: '10px', padding: '1.5rem', boxShadow: '0 1px 6px rgba(0,0,0,0.06)', marginBottom: '1.25rem' }}>
                <SectionHead label="Campaign Goals" color={T.navy} />

                <FieldGroup label="Primary Advertising Objective" required>
                  <select value={form.objective} onChange={set('objective')} style={{ ...inputSty(errors.objective), cursor: 'pointer' }}>
                    <option value="">Select an objective…</option>
                    {OBJECTIVES.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                  {errors.objective && <div style={{ fontSize: '0.77rem', color: T.accent, marginTop: '3px' }}>{errors.objective}</div>}
                </FieldGroup>

                <FieldGroup label="Target Audience / Geographic Reach" required>
                  <CheckGroup options={AUDIENCE_OPTIONS} values={form.audiences} onChange={toggle('audiences')} />
                  {errors.audiences && <div style={{ fontSize: '0.77rem', color: T.accent, marginTop: '6px' }}>{errors.audiences}</div>}
                </FieldGroup>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1rem' }}>
                  <FieldGroup label="Monthly Budget Range" required>
                    <select value={form.budget} onChange={set('budget')} style={{ ...inputSty(errors.budget), cursor: 'pointer' }}>
                      <option value="">Select a range…</option>
                      {BUDGET_OPTIONS.map(b => <option key={b} value={b}>{b}</option>)}
                    </select>
                    {errors.budget && <div style={{ fontSize: '0.77rem', color: T.accent, marginTop: '3px' }}>{errors.budget}</div>}
                  </FieldGroup>
                  <FieldGroup label="Campaign Duration">
                    <select value={form.duration} onChange={set('duration')} style={{ ...inputSty(), cursor: 'pointer' }}>
                      <option value="">Select duration…</option>
                      {DURATION_OPTIONS.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </FieldGroup>
                </div>

                <FieldGroup label="Preferred Start Date">
                  <input type="date" value={form.startDate} onChange={set('startDate')} style={inputSty()} />
                </FieldGroup>
              </div>

              {/* ── Ad Preferences ── */}
              <div style={{ background: T.white, borderRadius: '10px', padding: '1.5rem', boxShadow: '0 1px 6px rgba(0,0,0,0.06)', marginBottom: '1.25rem' }}>
                <SectionHead label="Ad Preferences" color="#555" />

                <FieldGroup label="Preferred Ad Format(s)">
                  <CheckGroup options={FORMAT_OPTIONS} values={form.formats} onChange={toggle('formats')} />
                </FieldGroup>

                <FieldGroup label="Preferred Topic Categories">
                  <CheckGroup options={CATEGORY_OPTIONS} values={form.categories} onChange={toggle('categories')} />
                </FieldGroup>
              </div>

              {/* ── Additional Details ── */}
              <div style={{ background: T.white, borderRadius: '10px', padding: '1.5rem', boxShadow: '0 1px 6px rgba(0,0,0,0.06)', marginBottom: '1.75rem' }}>
                <SectionHead label="Additional Details" color="#555" />

                <FieldGroup label="What are you advertising?">
                  <textarea
                    value={form.description}
                    onChange={set('description')}
                    rows={3}
                    style={{ ...inputSty(), resize: 'vertical' }}
                    placeholder="Describe the product, service, event, or cause you want to promote — include key selling points or any specific message you want to convey."
                  />
                </FieldGroup>

                <FieldGroup label="Special Requirements or Notes">
                  <textarea
                    value={form.notes}
                    onChange={set('notes')}
                    rows={3}
                    style={{ ...inputSty(), resize: 'vertical' }}
                    placeholder="Any specific demographics, exclusions, creative assets you already have, or other requirements we should know about."
                  />
                </FieldGroup>

                <FieldGroup label="How did you hear about PubSilo?">
                  <select value={form.hearAbout} onChange={set('hearAbout')} style={{ ...inputSty(), cursor: 'pointer' }}>
                    <option value="">Select one</option>
                    {HEAR_OPTIONS.map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                </FieldGroup>
              </div>

              {serverError && (
                <div style={{ background: '#fdecea', border: '1px solid #f5c6cb', borderRadius: '6px', padding: '0.75rem 1rem', marginBottom: '1rem', color: '#721c24', fontSize: '0.88rem' }}>
                  {serverError}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                style={{ width: '100%', padding: '0.9rem', background: loading ? '#999' : T.navy, color: T.white, border: 'none', borderRadius: '8px', fontSize: '1rem', fontWeight: '700', cursor: loading ? 'not-allowed' : 'pointer', letterSpacing: '0.03em' }}
              >
                {loading ? 'Sending…' : 'Submit Advertising Inquiry'}
              </button>

              
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default AdvertisePage;
